import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!slug) return;
    api.get(`/blog/posts/${encodeURIComponent(slug)}`)
      .then(({ data }) => setPost(data))
      .catch((e) => { const m = extractError(e); setError(m); toast.error(m); });
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-14">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="mt-6 paper-card p-8 text-center">
            <h1 className="font-heading font-bold text-sage-900 text-xl">Post not found</h1>
            <p className="mt-2 text-sm text-stone2-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-sage-600" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone2-50">
      <SEO
        title={`${post.title} · AutismBabysitters`}
        description={post.excerpt || post.title}
        canonical={`https://autismbabysitters.com/blog/${post.slug}`}
        image={post.cover_image_url || undefined}
      />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid={`blog-post-${post.slug}`}>
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold mb-6 hover:text-sage-900">
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>

        <div className="text-xs text-stone2-500 flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          {post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
          {post.author_name && <span>· by {post.author_name}</span>}
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-sage-900 leading-tight" data-testid="blog-post-title">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="mt-4 text-lg text-stone2-600 leading-relaxed">{post.excerpt}</p>
        )}
        {post.cover_image_url && (
          <img src={post.cover_image_url} alt={post.title}
            className="mt-8 rounded-2xl w-full max-h-[420px] object-cover" />
        )}

        <div className="mt-10 blog-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_md || ""}</ReactMarkdown>
        </div>
      </article>
      <Footer />
    </div>
  );
}
